import { api } from '@/lib/api'

export interface FiscalSettings {
  focus_id?: string
  focus_environment: 'sandbox' | 'production'
  company_cnpj: string
  company_name: string
  company_fantasy_name?: string
  municipal_inscription: string
  state_inscription?: string
  regime_tributario: number
  address_street: string
  address_number: string
  address_complement?: string
  address_neighborhood: string
  address_city: string
  address_uf: string
  address_zip: string
  company_email: string
  company_phone?: string
  service_item_code: string
  municipal_service_code?: string
  tax_percentage: number
  iss_retained: boolean
  // Campos RPS e CNAE (migration 031)
  cnae_code?: string
  codigo_tributacao_municipio?: string
  rps_serie?: string
  rps_tipo?: 1 | 2 | 3
  rps_last_number?: number
  certificate_expires_at?: string
  has_token?: boolean
  has_token_sandbox?: boolean
}

export const invoiceService = {
  getFiscalSettings(companyId: string): Promise<FiscalSettings | null> {
    return api.get<FiscalSettings | null>(`/companies/${companyId}/invoices/fiscal-settings`)
  },

  saveFiscalSettings(companyId: string, settings: FiscalSettings): Promise<FiscalSettings> {
    return api.post<FiscalSettings>(`/companies/${companyId}/invoices/fiscal-settings`, settings)
  },

  async uploadCertificate(companyId: string, file: File, password: string): Promise<{ certificate_expires_at: string }> {
    const token = localStorage.getItem('seven_analytics_token')
    const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'
    const formData = new FormData()
    formData.append('certificate', file)
    formData.append('password', password)

    const res = await fetch(`${baseUrl}/companies/${companyId}/invoices/fiscal-settings/certificate`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Erro ao enviar certificado' }))
      throw new Error(err.error ?? 'Erro ao enviar certificado')
    }
    return res.json() as Promise<{ certificate_expires_at: string }>
  },

  emitInvoice(companyId: string, movementId: string): Promise<{ message: string; ref: string }> {
    return api.post(`/companies/${companyId}/invoices/movements/${movementId}/invoices`, {})
  },

  syncInvoice(companyId: string, ref: string): Promise<any> {
    return api.get(`/companies/${companyId}/invoices/${ref}/sync`)
  },

  cancelInvoice(companyId: string, ref: string, justificativa: string): Promise<void> {
    return api.delete(`/companies/${companyId}/invoices/${ref}`, {
      body: JSON.stringify({ justificativa })
    })
  },

  listInvoices(
    companyId: string,
    filters?: {
      search?: string
      status?: string
      date_from?: string
      date_to?: string
      movement_id?: string
      page?: number
      limit?: number
    }
  ): Promise<{ data: any[]; total: number }> {
    const params = new URLSearchParams()
    if (filters?.search) params.set('search', filters.search)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.date_from) params.set('date_from', filters.date_from)
    if (filters?.date_to) params.set('date_to', filters.date_to)
    if (filters?.movement_id) params.set('movement_id', filters.movement_id)
    if (filters?.page) params.set('page', String(filters.page))
    if (filters?.limit) params.set('limit', String(filters.limit))
    const qs = params.toString()
    const url = `/companies/${companyId}/invoices${qs ? `?${qs}` : ''}`
    return api.get<{ data: any[]; total: number }>(url)
  },
}
